// =============================================================================
// Normalización de errores de API (hallazgos A4 / A5 resueltos)
// -----------------------------------------------------------------------------
// Todo error que sale de `@/services/http` es una instancia de `ApiError`:
//   { status, code, message, data, response, original }
// - `message` ya extraído del backend (string, {error|message|msg|detail} o null).
// - `response` conserva la respuesta original de Axios para compatibilidad con
//   el código que filtraba `error.response?.data?.error`.
// - `getErrorMessage(error, fallback)` devuelve SIEMPRE un string listo para
//   mostrar (fin del patrón repetido/inconsistente de A4).
// =============================================================================
export class ApiError extends Error {
  constructor(message, { status = null, code = null, data = null, response = null, original = null } = {}) {
    super(message || 'Error en la solicitud');
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
    this.response = response;
    this.original = original;
  }
}

/** Extrae el mensaje legible de un payload de error (string, objeto o null). */
function extractMessage(payload) {
  if (!payload) return '';
  if (typeof payload === 'string') return payload;
  if (typeof payload === 'object') {
    return (
      payload.error ||
      payload.message ||
      payload.msg ||
      payload.detail ||
      payload.title ||
      ''
    );
  }
  return '';
}

/**
 * Normaliza CUALQUIER error (Axios, string, objeto, Error genérico) a ApiError.
 * @param {*} error
 * @param {string} fallback Mensaje si no se pudo extraer nada del backend.
 */
export function normalizeApiError(error, fallback = 'Error en la solicitud') {
  if (error instanceof ApiError) return error;

  // Error de Axios (respuesta con status, timeout, sin red…)
  if (error && error.isAxiosError) {
    const { response, request, code } = error;
    const status = response ? response.status : null;
    const payload = response ? response.data : null;
    let message = extractMessage(payload);

    if (!message) {
      if (!response) {
        message = request ? 'Sin respuesta del servidor (timeout o conexión interrumpida).' : 'No se pudo conectar con el servidor.';
      } else if (status === 401) {
        message = 'Sesión expirada o credenciales inválidas.';
      } else if (status === 403) {
        message = 'No tienes permisos para realizar esta acción.';
      } else if (status === 404) {
        message = 'El recurso solicitado no existe.';
      } else if (status >= 500) {
        message = 'Error interno del servidor.';
      } else {
        message = `Error en la solicitud (${status || 'desconocido'}).`;
      }
    }

    return new ApiError(message || fallback, {
      status,
      code: payload?.code || code || null,
      data: payload,
      response,
      original: error,
    });
  }

  // Error lanzado como string (p. ej. "Respuesta de servidor inválida")
  if (typeof error === 'string') {
    return new ApiError(error || fallback, { original: error });
  }

  // Error lanzado como objeto/instancia de Error genérica
  if (error && typeof error === 'object') {
    if (error.message) {
      return new ApiError(String(error.message), { code: error.code, original: error });
    }
    const msg = extractMessage(error);
    if (msg) return new ApiError(msg, { data: error, original: error });
  }

  return new ApiError(fallback, { original: error });
}

/** Mensaje de error listo para mostrar (string seguro). */
export function getErrorMessage(error, fallback = 'Error inesperado.') {
  return normalizeApiError(error, fallback).message;
}